import { jsPDF } from 'jspdf';
import { format } from 'date-fns';
import { STANDARD_CLAUSES } from './agreement-templates';

/**
 * Generate an NDIS Service Agreement PDF
 */
export function generateServiceAgreementPDF(agreementData, settings) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  // Blue color matching Easy Invoice theme
  const easyInvoiceBlue = [37, 86, 168]; // #2556a8 in RGB
  const lightBlue = [235, 241, 251]; // Light blue for backgrounds
  let yPos = 20;


  // Helper function to check if new page is needed
  function checkNewPage(neededSpace = 30) {
    if (yPos + neededSpace > pageHeight - 60) {
      doc.addPage();
      yPos = 20; // Start from top
      return true;
    }
    return false;
  }

  // Start content from top
  yPos = 30;

  // ==================== HEADER SECTION ====================

  // Agreement title
  doc.setFontSize(24);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(...easyInvoiceBlue);
  doc.text('NDIS SERVICE AGREEMENT', pageWidth / 2, yPos, { align: 'center' });

  yPos += 15;
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100); // Gray text
  doc.text('Professional Support Services Agreement', pageWidth / 2, yPos, { align: 'center' });

  yPos += 20;

  // Agreement metadata in a styled box
  doc.setFillColor(...lightBlue);
  doc.roundedRect(15, yPos - 5, pageWidth - 30, 25, 5, 5, 'F');

  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.setFont(undefined, 'bold');
  doc.text(`Agreement Number: ${agreementData.agreementNumber || 'SA-' + Date.now()}`, 20, yPos + 5);
  doc.text(`Date Generated: ${format(new Date(), 'dd MMMM yyyy')}`, 20, yPos + 12);

  doc.text(`Participant: ${agreementData.participant?.name || ''}`, pageWidth - 20, yPos + 5, { align: 'right' });
  doc.text(`Provider: ${agreementData.provider?.name || ''}`, pageWidth - 20, yPos + 12, { align: 'right' });

  yPos += 35;

  // ==================== PARTICIPANT INFORMATION ====================
  checkNewPage(60);

  doc.setFillColor(...easyInvoiceBlue);
  doc.rect(15, yPos, pageWidth - 30, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('PARTICIPANT INFORMATION', 20, yPos + 8);

  yPos += 20;

  if (agreementData.participant) {
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');

    doc.text('Full Name:', 20, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(agreementData.participant.name, 80, yPos);
    yPos += 8;

    doc.setFont(undefined, 'bold');
    doc.text('NDIS Number:', 20, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(agreementData.participant.ndisNumber || 'Not provided', 80, yPos);
    yPos += 8;

    if (agreementData.participant.dateOfBirth) {
      doc.setFont(undefined, 'bold');
      doc.text('Date of Birth:', 20, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(format(new Date(agreementData.participant.dateOfBirth), 'dd MMMM yyyy'), 80, yPos);
      yPos += 8;
    }

    if (agreementData.participant.contact.phone) {
      doc.setFont(undefined, 'bold');
      doc.text('Phone:', 20, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(agreementData.participant.contact.phone, 80, yPos);
      yPos += 8;
    }

    if (agreementData.participant.contact.email) {
      doc.setFont(undefined, 'bold');
      doc.text('Email:', 20, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(agreementData.participant.contact.email, 80, yPos);
      yPos += 8;
    }
  }

  yPos += 10;

  // ==================== PROVIDER INFORMATION ====================
  checkNewPage(80);

  doc.setFillColor(...easyInvoiceBlue);
  doc.rect(15, yPos, pageWidth - 30, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('PROVIDER INFORMATION', 20, yPos + 8);

  yPos += 20;

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');

  doc.text('Provider Name:', 20, yPos);
  doc.setFont(undefined, 'normal');
  doc.text(agreementData.provider.name || 'Not provided', 80, yPos);
  yPos += 8;

  doc.setFont(undefined, 'bold');
  doc.text('ABN:', 20, yPos);
  doc.setFont(undefined, 'normal');
  doc.text(agreementData.provider.abn || 'Not provided', 80, yPos);
  yPos += 8;

  if (agreementData.provider.contactPerson) {
    doc.setFont(undefined, 'bold');
    doc.text('Contact Person:', 20, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(agreementData.provider.contactPerson, 80, yPos);
    yPos += 8;
  }

  if (agreementData.provider.phone) {
    doc.setFont(undefined, 'bold');
    doc.text('Phone:', 20, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(agreementData.provider.phone, 80, yPos);
    yPos += 8;
  }

  if (agreementData.provider.email) {
    doc.setFont(undefined, 'bold');
    doc.text('Email:', 20, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(agreementData.provider.email, 80, yPos);
    yPos += 8;
  }

  // Provider address
  const addressParts = [
    agreementData.provider.address.street,
    agreementData.provider.address.suburb,
    agreementData.provider.address.state,
    agreementData.provider.address.postcode
  ].filter(Boolean);

  if (addressParts.length > 0) {
    doc.setFont(undefined, 'bold');
    doc.text('Address:', 20, yPos);
    doc.setFont(undefined, 'normal');
    const addressText = addressParts.join(', ');
    const addressLines = doc.splitTextToSize(addressText, pageWidth - 100);
    doc.text(addressLines, 80, yPos);
    yPos += addressLines.length * 6;
  }

  yPos += 15;

  // ==================== SUPPORT DETAILS ====================
  checkNewPage(100);

  doc.setFillColor(...easyInvoiceBlue);
  doc.rect(15, yPos, pageWidth - 30, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('SUPPORT DETAILS', 20, yPos + 8);

  yPos += 20;

  // Template information
  if (agreementData.template) {
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text('Agreement Type:', 20, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(agreementData.template.name, 80, yPos);
    yPos += 8;

    doc.setFont(undefined, 'bold');
    doc.text('Category:', 20, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(agreementData.template.category, 80, yPos);
    yPos += 12;
  }

  // Support items table
  if (agreementData.supportItems && agreementData.supportItems.length > 0) {
    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...easyInvoiceBlue);
    doc.text('Support Items & Services', 20, yPos);
    yPos += 12;

    // Table headers
    doc.setFillColor(...lightBlue);
    doc.rect(15, yPos - 5, pageWidth - 30, 15, 'F');

    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Item Code', 20, yPos + 5);
    doc.text('Description', 65, yPos + 5);
    doc.text('Frequency', 130, yPos + 5);
    doc.text('Rate', pageWidth - 30, yPos + 5, { align: 'right' });

    yPos += 20;

    agreementData.supportItems.forEach((item, index) => {
      checkNewPage(25);

      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);

      // Item code
      doc.text(item.itemNumber, 20, yPos);

      // Description (wrapped)
      const descLines = doc.splitTextToSize(item.name, 60);
      doc.text(descLines, 65, yPos);

      // Frequency details
      let frequencyText = '';
      if (item.frequency === 'single') {
        frequencyText = 'Single session';
      } else {
        frequencyText = `${item.sessionsPerPeriod} × ${item.frequency}`;
      }
      frequencyText += `\n${item.hoursPerSession}h each`;
      doc.text(frequencyText, 130, yPos);

      // Rate
      doc.text(`$${(item.pricing.hourlyRate || 0).toFixed(2)}/hr`, pageWidth - 30, yPos, { align: 'right' });

      // Service location
      yPos += Math.max(descLines.length * 4, 12);
      doc.setFont(undefined, 'italic');
      doc.setTextColor(100, 100, 100);
      const location = item.location === 'Other' ? item.otherLocation : item.location;
      doc.text(`Location: ${location}`, 65, yPos);

      // Special requirements
      if (item.specialRequirements) {
        yPos += 6;
        const reqLines = doc.splitTextToSize(`Requirements: ${item.specialRequirements}`, pageWidth - 85);
        reqLines.forEach((line, i) => {
          if (yPos > doc.internal.pageSize.getHeight() - 30) {
            doc.addPage();
            yPos = 30;
          }
          doc.text(line, 65, yPos);
          if (i < reqLines.length - 1) yPos += 5;
        });
      }

      yPos += 15;
      doc.setTextColor(0, 0, 0);

      // Add separator line
      if (index < agreementData.supportItems.length - 1) {
        doc.setDrawColor(200, 200, 200);
        doc.line(20, yPos, pageWidth - 20, yPos);
        yPos += 10;
      }
    });
  }

  yPos += 15;

  // ==================== PRICING SUMMARY ====================
  checkNewPage(80);

  doc.setFillColor(...easyInvoiceBlue);
  doc.rect(15, yPos, pageWidth - 30, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('PRICING SUMMARY', 20, yPos + 8);

  yPos += 20;

  // Calculate total costs
  let totalWeekly = 0;
  let totalMonthly = 0;

  if (agreementData.supportItems) {
    agreementData.supportItems.forEach(item => {
      const rate = parseFloat(item.pricing.hourlyRate) || 0;
      const hours = parseFloat(item.hoursPerSession) || 0;
      const sessions = parseFloat(item.sessionsPerPeriod) || 1;

      const costPerSession = rate * hours;

      if (item.frequency === 'single') {
        // One-time cost, don't add to ongoing totals
      } else if (item.frequency === 'daily') {
        totalWeekly += costPerSession * sessions * 7;
      } else if (item.frequency === 'weekly') {
        totalWeekly += costPerSession * sessions;
      } else if (item.frequency === 'fortnightly') {
        totalWeekly += (costPerSession * sessions) / 2;
      } else if (item.frequency === 'monthly') {
        totalWeekly += (costPerSession * sessions) / 4.33;
      }
    });

    totalMonthly = totalWeekly * 4.33;
  }

  // Cost summary table
  doc.setFillColor(...lightBlue);
  doc.rect(15, yPos - 5, pageWidth - 30, 35, 'F');

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');

  doc.text('Estimated Ongoing Costs:', 20, yPos + 5);
  doc.text('Weekly:', 20, yPos + 15);
  doc.text(`$${totalWeekly.toFixed(2)}`, pageWidth - 30, yPos + 15, { align: 'right' });
  doc.text('Monthly:', 20, yPos + 25);
  doc.text(`$${totalMonthly.toFixed(2)}`, pageWidth - 30, yPos + 25, { align: 'right' });

  yPos += 40;
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('* Costs are estimates based on agreed rates and frequencies. Actual costs may vary.', 20, yPos);
  doc.text(`* Service region: ${agreementData.pricing.region.charAt(0).toUpperCase() + agreementData.pricing.region.slice(1)}`, 20, yPos + 8);

  yPos += 25;

  // ==================== AGREEMENT PERIOD ====================
  checkNewPage(50);

  doc.setFillColor(...easyInvoiceBlue);
  doc.rect(15, yPos, pageWidth - 30, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('AGREEMENT PERIOD', 20, yPos + 8);

  yPos += 20;

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');

  if (agreementData.period.startDate) {
    doc.text('Commencement Date:', 20, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(format(new Date(agreementData.period.startDate), 'dd MMMM yyyy'), 100, yPos);
    yPos += 8;
  }

  if (agreementData.period.ongoingUntilCancelled) {
    doc.setFont(undefined, 'bold');
    doc.text('Agreement Duration:', 20, yPos);
    doc.setFont(undefined, 'normal');
    doc.text('No fixed end date - ongoing agreement', 100, yPos);
    yPos += 8;
  } else if (agreementData.period.endDate) {
    doc.setFont(undefined, 'bold');
    doc.text('End Date:', 20, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(format(new Date(agreementData.period.endDate), 'dd MMMM yyyy'), 100, yPos);
    yPos += 8;
  }

  // Review date removed - can be inferred by agreement end date or at discretion
  doc.setFont(undefined, 'bold');
  doc.text('Review Schedule:', 20, yPos);
  doc.setFont(undefined, 'normal');
  doc.text('At client/worker discretion', 100, yPos);
  yPos += 8;

  doc.setFont(undefined, 'bold');
  doc.text('Payment Terms:', 20, yPos);
  doc.setFont(undefined, 'normal');
  doc.text(agreementData.terms.paymentTerms, 100, yPos);
  yPos += 8;

  doc.setFont(undefined, 'bold');
  doc.text('Cancellation Notice:', 20, yPos);
  doc.setFont(undefined, 'normal');
  doc.text(agreementData.terms.cancellationNotice, 100, yPos);
  yPos += 15;

  // ==================== TERMS & CONDITIONS ====================
  checkNewPage(100);

  doc.setFillColor(...easyInvoiceBlue);
  doc.rect(15, yPos, pageWidth - 30, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('TERMS & CONDITIONS', 20, yPos + 8);

  yPos += 20;

  // Selected standard clauses
  if (agreementData.terms.selectedClauses && agreementData.terms.selectedClauses.length > 0) {
    agreementData.terms.selectedClauses.forEach(clauseId => {
      const clause = STANDARD_CLAUSES[clauseId];
      if (clause) {
        checkNewPage(40);

        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`${clause.title}`, 20, yPos);
        yPos += 10;

        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        const contentLines = doc.splitTextToSize(clause.content, pageWidth - 40);
        doc.text(contentLines, 20, yPos);
        yPos += (contentLines.length * 4) + 10;
      }
    });
  }

  // Custom clauses
  if (agreementData.terms.customClauses && agreementData.terms.customClauses.length > 0) {
    agreementData.terms.customClauses.forEach((customClause, index) => {
      checkNewPage(30);

      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');

      // Handle both old string format and new object format
      const clauseTitle = typeof customClause === 'string'
        ? `Custom Clause ${index + 1}`
        : customClause.title;
      const clauseContent = typeof customClause === 'string'
        ? customClause
        : customClause.content;

      doc.text(clauseTitle, 20, yPos);
      yPos += 10;

      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const clauseLines = doc.splitTextToSize(clauseContent, pageWidth - 40);
      doc.text(clauseLines, 20, yPos);
      yPos += (clauseLines.length * 4) + 10;
    });
  }

  // ==================== SIGNATURE SECTION ====================
  checkNewPage(80);

  doc.setFillColor(...easyInvoiceBlue);
  doc.rect(15, yPos, pageWidth - 30, 12, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('AGREEMENT SIGNATURES', 20, yPos + 8);

  yPos += 30;

  // Signature lines
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');

  // Participant signature
  doc.text('PARTICIPANT:', 20, yPos);
  yPos += 20;
  doc.line(20, yPos, 90, yPos);
  yPos += 8;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(10);
  doc.text(`Print Name: ${agreementData.participant?.name || '_'.repeat(30)}`, 20, yPos);
  yPos += 6;
  doc.text('Date: _______________', 20, yPos);

  // Provider signature (right side)
  yPos -= 34;
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('PROVIDER:', 120, yPos);
  yPos += 20;
  doc.line(120, yPos, 190, yPos);
  yPos += 8;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(10);
  doc.text(`Print Name: ${agreementData.provider?.contactPerson || '_'.repeat(30)}`, 120, yPos);
  yPos += 6;
  doc.text('Date: _______________', 120, yPos);

  yPos += 30;

  // Footer on last page
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated by Easy Invoice • Agreement ${agreementData.agreementNumber || 'SA-' + Date.now()}`,
    pageWidth / 2, pageHeight - 60, { align: 'center' });
  doc.text(`Generated on ${format(new Date(), 'dd MMMM yyyy')} at ${format(new Date(), 'HH:mm')}`,
    pageWidth / 2, pageHeight - 52, { align: 'center' });

  // Add page numbers to all pages
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
  }

  return doc;
}